import { memoize } from "lodash";
import { computed, observable, runInAction } from "mobx";
import {
  E_CLIENT_PAGE,
  TClientPageType,
  TCorePage,
  TGender,
  TGetKhattabaClients,
  TKhattabatClientCategory
} from "../../../Types";
import getCorePersonalStore from "../../core/core-personal-store";
import getKhattabatFilterStore from "../filter-store";
import getKhattabat101CStore from ".";
import { getKhattabaAiStore } from "../ai-stores/khattaba-ai-store";
import getKhattabatStore from "..";
import { isDefinedArray, reduce } from "et3-type-guards";
import { apiCallGetKhattabaClients } from "../../../api/khattbat-api/api-call-get-khattaba-clients";
import getCorePopUpStore from "../../core/core-popup-store";
import getCoreTimingStore from "../../core/core-timing-store";
import getCoreUIStore from "../../core/core-ui-store";
import getKhattabatSelectsStore from "../selects-store";
import { apiCallReactivateKhatabaClient } from "../../../api/khattbat-api/api-call-reactivate-khataba-client";
import { getDefaultDate } from "../../../helpers/et3-date-helper";

class Khattabat101CGeneralStore {
  clientsIDs = observable.box<number[]>([]);
  allAvailableClientsIds = observable.box<number[]>([]);
  clientPageType = observable.box<TClientPageType>(E_CLIENT_PAGE.BREACHE_LEVEL);
  conversationsClientType = observable.box<TKhattabatClientCategory>("PAID");
  clientToBeReactivatedDetails = observable.box<{
    userID: number;
  }>({
    userID: 0
  });

  getClientPageType = computed(() => {
    const isAiKhattaba = getCorePersonalStore().isKhattabaAiAdmin.get();
    if (isAiKhattaba) {
      return E_CLIENT_PAGE.ALL;
    }
    return this.clientPageType.get();
  });

  setClientPageType = (type: TClientPageType) => {
    getKhattabatFilterStore().resetFilters();
    runInAction(() => {
      this.clientPageType.set(type);
    });
  };

  getRequiresAttentionClients = computed((): number[] => {
    const ids = this.clientsIDs.get();
    const isAiKhattaba = getCorePersonalStore().isKhattabaAiAdmin.get();
    if (!isDefinedArray(ids) || isAiKhattaba) {
      return [];
    }

    return (
      ids
        .filter((userID) => {
          const Khattabat101CStore = getKhattabat101CStore(userID);

          const clientIsAlmostExpiredAndNo102Quality =
            Khattabat101CStore.clientIsAlmostExpiredAndNo102Quality.get();
          const hasUnreadMessagesSinceMoreThan18Hours =
            Khattabat101CStore.hasUnreadMessagesSinceMoreThan18Hours.get();
          const hasNoRCMessageSinceMoreThan54Hours =
            Khattabat101CStore.hasNoRCMessageSinceMoreThan54Hours.get();
          return (
            clientIsAlmostExpiredAndNo102Quality ||
            hasUnreadMessagesSinceMoreThan18Hours ||
            hasNoRCMessageSinceMoreThan54Hours
          );
        })

        // sort based on the required ordering
        .sort((a, b) => {
          const clientAStore = getKhattabat101CStore(a);
          const clientBStore = getKhattabat101CStore(b);

          // 1. Client expiry in < 7 days AND no quality 102
          if (
            clientAStore.clientIsAlmostExpiredAndNo102Quality.get() &&
            !clientBStore.clientIsAlmostExpiredAndNo102Quality.get()
          )
            return -1;
          if (
            !clientAStore.clientIsAlmostExpiredAndNo102Quality.get() &&
            clientBStore.clientIsAlmostExpiredAndNo102Quality.get()
          )
            return 1;

          // 2. Client has unread messages > 18 hours
          if (
            clientAStore.hasUnreadMessagesSinceMoreThan18Hours.get() &&
            !clientBStore.hasUnreadMessagesSinceMoreThan18Hours.get()
          )
            return -1;
          if (
            !clientAStore.hasUnreadMessagesSinceMoreThan18Hours.get() &&
            clientBStore.hasUnreadMessagesSinceMoreThan18Hours.get()
          )
            return 1;

          // 3. RC has not sent message > 54 hours
          if (
            clientAStore.hasNoRCMessageSinceMoreThan54Hours.get() &&
            !clientBStore.hasNoRCMessageSinceMoreThan54Hours.get()
          )
            return -1;
          if (
            !clientAStore.hasNoRCMessageSinceMoreThan54Hours.get() &&
            clientBStore.hasNoRCMessageSinceMoreThan54Hours.get()
          )
            return 1;

          return 0;
        })
    );
  });

  getClients = computed((): number[] => {
    const isAiKhattaba = getCorePersonalStore().isKhattabaAiAdmin.get();
    if (isAiKhattaba) {
      const aiClients = getKhattabaAiStore().clients.get();
      getKhattabatStore().updateAlkhattabaUUID(aiClients);

      aiClients.forEach((client) => {
        getKhattabat101CStore(client.userID).setClientData(client);
      });
      return aiClients.map((client) => client.userID);
    }

    const isAllClients = this.getClientPageType.get() === E_CLIENT_PAGE.ALL;
    if (!isAllClients) {
      return this.getRequiresAttentionClients.get();
    }

    if (getKhattabatFilterStore().searchingClients.get()) {
      return getKhattabatFilterStore().filteredArray.get();
    }
    return this.clientsIDs.get();
  });

  getCurrentUserIds = () => {
    return this.getClients.get();
  };

  setKhattaClients = (ids: number[]) => {
    runInAction(() => {
      this.clientsIDs.set(ids);
    });
  };

  updateClients = (ids: number[]) => {
    const current = this.clientsIDs.get();
    ids.forEach((id) => {
      if (!current.includes(id)) {
        current.push(id);
      }
    });
    this.setKhattaClients(current);
  };

  setAllAvailableClientsIds = (clients: TGetKhattabaClients[]) => {
    const Ids = clients.map((client) => client.userID);

    runInAction(() => {
      const currentIds = this.allAvailableClientsIds.get();
      Ids.forEach((id) => {
        if (!currentIds.includes(id)) {
          currentIds.push(id);
        }
      });
      this.allAvailableClientsIds.set(currentIds);
    });
  };

  getKhattabaClients = async ({
    gender,
    pageName
  }: {
    gender: TGender;
    pageName: TCorePage;
  }) => {
    getCoreUIStore().setIsLoading(pageName, true);
    const { status, code, data } = await apiCallGetKhattabaClients({
      gender,
      shouldUpdateUnread: false
    });
    getCoreUIStore().setIsLoading(pageName, false);
    if (status === "OK") {
      const activeKhattabat = reduce(() => data, []);

      getKhattabatStore().updateAlkhattabaUUID(activeKhattabat);
      activeKhattabat.forEach((client) => {
        getKhattabat101CStore(client.userID).setClientData(client);
      });
      this.setKhattaClients(activeKhattabat.map((client) => client.userID));
      getKhattabatSelectsStore().initializeAndClearMarksStateWithClients(
        "clientPage"
      );
      getCoreTimingStore().initializeKhattabatPageMonitoring(pageName);
      this.setAllAvailableClientsIds(activeKhattabat);
      return;
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  getUnreadKhattabaClients = async ({ gender }: { gender: TGender }) => {
    const { status, code, data } = await apiCallGetKhattabaClients({
      gender,
      shouldUpdateUnread: true
    });
    if (status === "OK") {
      const activeKhattabat = reduce(() => data, []);
      activeKhattabat.forEach((client) => {
        getKhattabatStore().updateAlkhattabaUUID(activeKhattabat);
        getKhattabat101CStore(client.userID).setClientData(client);
      });
      this.updateClients(activeKhattabat.map((client) => client.userID));
      this.setAllAvailableClientsIds(activeKhattabat);
      return;
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  setConversationsClientType = (clientType: TKhattabatClientCategory) => {
    runInAction(() => {
      this.conversationsClientType.set(clientType);
    });
  };

  setClientToBeReactivatedDetails = ({ userID }: { userID: number }) => {
    runInAction(() => {
      this.clientToBeReactivatedDetails.set({ userID });
    });
  };

  async confirmeRactivatingKhatabaClient() {
    const shouldBeDisabled =
      getCoreUIStore().isPageLoading.get("KHATTABAT_CLIENTS");
    if (shouldBeDisabled) {
      return;
    }
    const { userID } = this.clientToBeReactivatedDetails.get();
    getCoreUIStore().setIsLoading("KHATTABAT_CLIENTS", true);
    const { data, status, code } = await apiCallReactivateKhatabaClient({
      userID
    });
    if (status === "OK") {
      const reactivateSentDateReduced = reduce(
        () => data.ReactivationSentDate,
        getDefaultDate()
      );
      getKhattabat101CStore(userID).setKhattabaClientTobeReactivatedClientSide(
        reactivateSentDateReduced
      );
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
    getCoreUIStore().setIsLoading("KHATTABAT_CLIENTS", false);
    getCorePopUpStore().closeConfirmationPopUps();
  }
}
export const getKhattabat101CGeneralStore = memoize(
  () => new Khattabat101CGeneralStore(),
  () => 1
);
export default getKhattabat101CGeneralStore;
